import { Injectable, inject, resource, computed, signal } from '@angular/core';
import { collection, query, where, getDocs, getDoc, setDoc, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import { AccessLogService } from './access-log.service';
import { Sensor, SensorCategoryName } from '../../shared/interfaces';

export interface SensorCreateInput {
  id: string; // Sensor ID needs to be specified manually as it usually matches the device MAC/EUI
  organizationId: string;
  categoryName: SensorCategoryName;
  siteId?: string | null;
}

export interface SensorUpdateInput {
  organizationId?: string;
  categoryName?: SensorCategoryName;
  siteId?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SensorMasterService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly accessLogService = inject(AccessLogService);

  private readonly manualError = signal<string | null>(null);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private readonly sensorsResource = resource({
    params: () => ({ 
      role: this.authService.userRole(),
      orgId: this.authService.userOrganizationId()
    }),
    loader: async ({ params }) => {
      if (!params.role) {
        return [];
      }
      try {
        const sensorsRef = collection(this.firestore, 'sensors');
        let q = query(sensorsRef);

        if (params.role !== 'super_admin') {
          if (!params.orgId) return [];
          q = query(sensorsRef, where('organizationId', '==', params.orgId));
        }

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            siteId: data['siteId'] ?? null
          } as Sensor;
        });
      } catch (err) {
        console.error('センサー一覧の取得に失敗しました:', err);
        throw new Error('センサー一覧の取得に失敗しました');
      }
    }
  });

  public readonly sensors = computed<Sensor[]>(() => {
    return this.sensorsResource.hasValue() ? this.sensorsResource.value() : [];
  });

  public readonly isLoading = computed(() => 
    this.sensorsResource.status() === 'loading' || 
    this.sensorsResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const manual = this.manualError();
    if (manual) return manual;
    const resErr = this.sensorsResource.error();
    if (resErr) return resErr instanceof Error ? resErr.message : 'センサー一覧の取得エラー';
    return null;
  });

  public reloadSensors(): void {
    this.manualError.set(null);
    this.sensorsResource.reload();
  }

  private checkMutationPermission(sensorOrgId?: string): void {
    const role = this.authService.userRole();
    if (role !== 'super_admin' && role !== 'org_admin') {
      throw new Error('権限がありません');
    }
    if (role === 'org_admin' && sensorOrgId && sensorOrgId !== this.authService.userOrganizationId()) {
      throw new Error('他組織のセンサーに対する操作権限がありません');
    }
  }

  public async createSensor(data: SensorCreateInput): Promise<void> {
    this.checkMutationPermission(data.organizationId);
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      const sensorRef = doc(this.firestore, 'sensors', data.id);
      const docSnap = await getDoc(sensorRef);
      if (docSnap.exists()) {
        throw new Error('指定されたセンサーIDは既に存在します');
      }
      await setDoc(sensorRef, {
        id: data.id,
        organizationId: data.organizationId,
        categoryName: data.categoryName,
        siteId: data.siteId || null,
        data: {},
        deletedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      status = 'error';
      let errorMessage = 'センサーの作成に失敗しました';
      if (err instanceof Error && (err.message.includes('存在します') || err.message.includes('権限'))) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('sensor.create', `sensors/${data.id}`, status);
    }
  }

  public async updateSensor(id: string, data: SensorUpdateInput): Promise<void> {
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      const sensorRef = doc(this.firestore, 'sensors', id);
      const docSnap = await getDoc(sensorRef);
      
      if (!docSnap.exists()) {
        throw new Error('対象のセンサーが見つかりません');
      }
      
      // 既存ドキュメントの organizationId に対して常に権限チェック
      const existingData = docSnap.data() as Sensor;
      this.checkMutationPermission(existingData.organizationId);

      // 新しい organizationId が指定され、かつ既存と異なる場合は移行先に対してもチェック
      if (data.organizationId && data.organizationId !== existingData.organizationId) {
        this.checkMutationPermission(data.organizationId);
      }

      const updatePayload: any = {
        ...data,
        updatedAt: serverTimestamp()
      };

      if (existingData.deletedAt === undefined) {
        updatePayload.deletedAt = null;
      }

      await updateDoc(sensorRef, updatePayload);
    } catch (err) {
      status = 'error';
      let errorMessage = 'センサーの更新に失敗しました';
      if (err instanceof Error && (err.message.includes('見つかりません') || err.message.includes('権限'))) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('sensor.update', `sensors/${id}`, status);
    }
  }

  public async deleteSensor(id: string): Promise<void> {
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      
      const sensorRef = doc(this.firestore, 'sensors', id);
      const docSnap = await getDoc(sensorRef);

      if (!docSnap.exists()) {
        throw new Error('対象のセンサーが見つかりません');
      }

      const targetSensor = docSnap.data() as Sensor;
      this.checkMutationPermission(targetSensor.organizationId);

      if (targetSensor.siteId) {
        throw new Error('現場に紐付いているセンサーは削除できません');
      }

      await deleteDoc(sensorRef);
    } catch (err) {
      status = 'error';
      let errorMessage = 'センサーの削除に失敗しました';
      if (err instanceof Error && (err.message.includes('削除できません') || err.message.includes('見つかりません') || err.message.includes('権限'))) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('sensor.delete', `sensors/${id}`, status);
    }
  }
}
